import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import { Inject } from 'inversify-props';
import { VeterinarianOfficeUsersService } from '@/services/veterinarian-office-user.service';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import { VeterinarianOfficeUser } from '@/models/DTO/veterinarian-office-user/veterinarian-office-user.model';
import { VeterinarianOfficeUsersActions } from '@/constants/actions.constants';
import { VeterinarianOfficeUsersMutations } from '@/constants/mutation.constants';
import { VeterinarianOfficeUserUpdateRequest } from '@/models/DTO/veterinarian-office-user/veterinarian-office-user-update-request.model';
import { VeterinarianUserGetters } from '@/constants/getters.constants';

export const VETERINARIAN_USER_MODULE_NAME = 'veterinarianUser';

class DefaultState {
  public showLoading = false;
  public showButtonLoading = false;
  public errorResponse: BaseAPIErrorModel | null = null;
  public currentVeterinarianOfficeUser: VeterinarianOfficeUser | null = null;
  public lockOrUnlockUserSuccess: boolean | null = null;
}

const defaultState = new DefaultState();

@Module({
  namespaced: true,
  name: VETERINARIAN_USER_MODULE_NAME
})
export default class VeterinarianOfficeUserModule extends VuexModule {
  //#region Services
  @Inject()
  private readonly _veterinarianOfficeUsersService!: VeterinarianOfficeUsersService;
  //#endregion

  //#region State
  public showLoading = defaultState.showLoading;
  public showButtonLoading = defaultState.showButtonLoading;
  public errorResponse: BaseAPIErrorModel | null = defaultState.errorResponse;
  public currentVeterinarianOfficeUser: VeterinarianOfficeUser | null =
    defaultState.currentVeterinarianOfficeUser;
  public lockOrUnlockUserSuccess: boolean | null =
    defaultState.lockOrUnlockUserSuccess;
  //#endregion

  // #region Getters
  public get [VeterinarianUserGetters.hasErrorMessages](): boolean {
    return (
      this.context.getters[VeterinarianUserGetters.errorMessages].length > 0
    );
  }

  public get [VeterinarianUserGetters.errorMessages](): string[] {
    return this.errorResponse?.getAllErrorMessages?.() ?? [];
  }
  //#endregion

  //#region Actions
  @Action
  public async [VeterinarianOfficeUsersActions.getByIdAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, null);

      const resp = await this._veterinarianOfficeUsersService.getById(id);

      // success
      this.context.commit(
        VeterinarianOfficeUsersMutations.setCurrentVeterinarianOfficeUserMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, e);
    } finally {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [VeterinarianOfficeUsersActions.updateVeterinarianOfficeUserAction](
    model: VeterinarianOfficeUserUpdateRequest
  ): Promise<void> {
    try {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showButtonLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, null);

      const resp = await this._veterinarianOfficeUsersService.update({
        ...model
      });

      // success
      this.context.commit(
        VeterinarianOfficeUsersMutations.setCurrentVeterinarianOfficeUserMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, e);
    } finally {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showButtonLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [VeterinarianOfficeUsersActions.lockoutVeterinarianOfficeUserAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, null);
      this.context.commit(
        VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation,
        null
      );

      await this._veterinarianOfficeUsersService.lockoutVeterinarianOfficeUser(
        id
      );

      // success
      this.context.commit(
        VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation,
        true
      );
    } catch (error) {
      // fail
      this.context.commit(
        VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation,
        false
      );

      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, e);
    } finally {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [VeterinarianOfficeUsersActions.unlockVeterinarianOfficeUserAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, null);
      this.context.commit(
        VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation,
        null
      );

      await this._veterinarianOfficeUsersService.unlockVeterinarianOfficeUser(
        id
      );

      // success
      this.context.commit(
        VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation,
        true
      );
    } catch (error) {
      // fail
      this.context.commit(
        VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation,
        false
      );

      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(VeterinarianOfficeUsersMutations.errorMutation, e);
    } finally {
      this.context.commit(
        VeterinarianOfficeUsersMutations.showLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [VeterinarianOfficeUsersActions.resetStateAction](): Promise<
    void
  > {
    this.context.commit(VeterinarianOfficeUsersMutations.resetStateMutation);
  }
  //#endregion

  //#region Mutations
  @Mutation
  private [VeterinarianOfficeUsersMutations.showLoadingMutation](
    data: boolean
  ) {
    this.showLoading = data;
  }

  @Mutation
  private [VeterinarianOfficeUsersMutations.showButtonLoadingMutation](
    data: boolean
  ) {
    this.showButtonLoading = data;
  }

  @Mutation
  private [VeterinarianOfficeUsersMutations.errorMutation](
    data: BaseAPIErrorModel | null
  ) {
    this.errorResponse = data;
  }

  @Mutation
  private [VeterinarianOfficeUsersMutations.setCurrentVeterinarianOfficeUserMutation](
    data: VeterinarianOfficeUser | null
  ) {
    this.currentVeterinarianOfficeUser = data;
  }

  @Mutation
  private [VeterinarianOfficeUsersMutations.setLockOrUnlockUserSuccessMutation](
    data: boolean | null
  ) {
    this.lockOrUnlockUserSuccess = data;
  }

  @Mutation
  private [VeterinarianOfficeUsersMutations.resetStateMutation]() {
    Object.assign(this, defaultState);
  }
  //#endregion
}
